import { cva, type VariantProps } from "class-variance-authority";
import { t } from "i18next";
import { CheckIcon } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "~/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { cn } from "~/lib/utils";

import { Icon } from "../Icon";

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
  "m-1 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300",
  {
    variants: {
      variant: {
        default: "border-foreground/10 text-foreground bg-card hover:bg-card/80",
        secondary:
          "border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        inverted: "inverted",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options: {
    /** The text to display for the option. */
    label: string;
    /** The unique value associated with the option. */
    value: string;
    /** Optional icon component to display alongside the option. */
    icon?: React.ComponentType<{ className?: string }>;
  }[];

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: string[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number;

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  container?: HTMLElement | null;

  label?: string;

  value?: string[];

  allLabel?: string;
}

export const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      onValueChange,
      value,
      defaultValue = [],
      placeholder = "Select options",
      label = "",
      maxCount = 3,
      modalPopover = false,
      allLabel = t("common.button.all"),
      className,
      container,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = React.useState<string[]>(defaultValue);
    const selectedValues = isControlled ? value! : internalValue;
    const triggerRef = React.useRef<HTMLButtonElement>(null);

    React.useImperativeHandle(ref, () => triggerRef.current!, []);

    const handleChange = (newValues: string[]) => {
      if (!isControlled) {
        setInternalValue(newValues);
      }
      onValueChange(newValues);
    };

    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [focusedOptionIndex, setFocusedOptionIndex] = React.useState(-1);

    React.useEffect(() => {
      if (isPopoverOpen) {
        setFocusedOptionIndex(0);
        const timer = setTimeout(() => {
          const firstOption = document.querySelector(
            '[role="listbox"] [role="option"]',
          ) as HTMLElement;
          if (firstOption) {
            firstOption.focus();
          }
        }, 100);
        return () => clearTimeout(timer);
      } else {
        setFocusedOptionIndex(-1);
      }
    }, [isPopoverOpen]);

    const toggleOption = (option: string) => {
      const newSelectedValues = selectedValues.includes(option)
        ? selectedValues.filter((value) => value !== option)
        : [...selectedValues, option];
      handleChange(newSelectedValues);
    };

    const handleClear = () => {
      handleChange([]);
    };

    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev);
    };

    const focusNextElementOutside = () => {
      const focusableElements = document.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])',
      );

      const currentElement = triggerRef.current;
      if (!currentElement) return;

      const currentIndex = Array.from(focusableElements).indexOf(currentElement);
      const nextElement = focusableElements[currentIndex + 1] as HTMLElement;

      if (nextElement) {
        setIsPopoverOpen(false);
        nextElement.focus();
      }
    };

    const toggleAll = () => {
      handleClear();
    };

    return (
      <div>
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={modalPopover}>
          <PopoverTrigger asChild>
            <Button
              ref={triggerRef}
              onClick={handleTogglePopover}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  handleTogglePopover();
                } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsPopoverOpen(true);
                }
              }}
              tabIndex={0}
              aria-haspopup="listbox"
              aria-expanded={isPopoverOpen}
              className={cn(
                "flex h-12 min-w-40 items-center justify-between border border-neutral-200 bg-white px-3 py-0.5 transition-all hover:bg-violet-50 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2",
                isPopoverOpen ? "rounded-b-none rounded-t-xl border-b-transparent" : "rounded-xl",
                className,
              )}
            >
              <div className="flex flex-1 flex-col items-start">
                <span className="details font-normal text-neutral-800">{label}</span>
                {selectedValues.length ? (
                  <div className="flex flex-row items-center">
                    <span className="body-sm font-normal text-black">
                      {selectedValues.slice(0, maxCount).map((value) => {
                        const option = options.find((o) => o.value === value);

                        if (!option) return null;

                        return option.label + " ";
                      })}
                    </span>
                    {selectedValues.length > maxCount && (
                      <span className="details ml-2 flex h-4 items-center justify-center rounded bg-neutral-200 px-1 py-0.5 text-xs font-normal text-black">
                        {`${t("searchLessonView.filterCount", { count: selectedValues.length - maxCount })}`}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="body-sm shrink-0 font-normal text-black">{placeholder}</span>
                )}
              </div>
              <div className="flex w-6 items-center justify-center">
                <Icon name="CHEVRONDOWN" className="size-6 text-black" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="z-[160] w-[var(--radix-popover-trigger-width)] !rounded-b-xl rounded-t-none border-t-0 p-0"
            align="start"
            onEscapeKeyDown={(e) => {
              e.stopPropagation();
              setIsPopoverOpen(false);
            }}
            sideOffset={-1}
            container={container}
            role="listbox"
            aria-multiselectable="true"
            aria-label={`${label || placeholder} options`}
          >
            <Command
              onKeyDown={(e) => {
                const totalOptions = options.length + 1;

                if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsPopoverOpen(false);
                  triggerRef.current?.focus();
                } else if (e.key === "ArrowDown") {
                  e.preventDefault();
                  const nextIndex = focusedOptionIndex + 1;
                  if (nextIndex >= totalOptions) {
                    focusNextElementOutside();
                  } else {
                    setFocusedOptionIndex(nextIndex);
                    const nextOption = document.querySelectorAll('[role="option"]')[
                      nextIndex
                    ] as HTMLElement;
                    if (nextOption) nextOption.focus();
                  }
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  const prevIndex = focusedOptionIndex - 1;
                  if (prevIndex < 0) {
                    setIsPopoverOpen(false);
                    triggerRef.current?.focus();
                  } else {
                    setFocusedOptionIndex(prevIndex);
                    const prevOption = document.querySelectorAll('[role="option"]')[
                      prevIndex
                    ] as HTMLElement;
                    if (prevOption) prevOption.focus();
                  }
                } else if (e.key === "Tab") {
                  e.preventDefault();
                  focusNextElementOutside();
                }
              }}
            >
              <CommandList>
                <CommandEmpty>No results found.</CommandEmpty>
                <CommandSeparator />
                <CommandGroup className="px-0">
                  {options.map((option, index) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <CommandItem
                        key={option.value}
                        onSelect={() => toggleOption(option.value)}
                        onFocus={() => setFocusedOptionIndex(index)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleOption(option.value);
                          } else if (e.key === "Tab") {
                            e.preventDefault();
                            focusNextElementOutside();
                          }
                        }}
                        tabIndex={0}
                        role="option"
                        aria-selected={isSelected}
                        aria-label={`${isSelected ? "Deselect" : "Select"} ${option.label}`}
                        className={cn(
                          "flex min-h-8 cursor-pointer items-center justify-between break-all rounded-none px-3 py-2 text-left hover:bg-violet-50 focus:bg-violet-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-violet-500",
                          isSelected ? "bg-violet-100 text-violet-950" : "bg-white text-black",
                        )}
                      >
                        <span className="body-sm flex-1 text-left">{option.label}</span>
                        <div className="ml-2 flex flex-shrink-0 items-center justify-center">
                          {isSelected && (
                            <CheckIcon className="!size-6 text-violet-950" strokeWidth={2} />
                          )}
                        </div>
                      </CommandItem>
                    );
                  })}
                  <CommandItem
                    key="all"
                    onSelect={toggleAll}
                    onFocus={() => setFocusedOptionIndex(options.length)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleAll();
                      } else if (e.key === "Tab") {
                        e.preventDefault();
                        focusNextElementOutside();
                      }
                    }}
                    tabIndex={0}
                    role="option"
                    aria-selected={selectedValues.length === options.length}
                    aria-label={
                      selectedValues.length === options.length
                        ? `Odznacz wszystkie ${placeholder}`
                        : `Zaznacz wszystkie ${placeholder}`
                    }
                    className="flex min-h-8 cursor-pointer items-center justify-between px-3 py-2 hover:bg-violet-50 focus:bg-violet-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-violet-500"
                  >
                    <span className="body-sm">{allLabel}</span>
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);

MultiSelect.displayName = "MultiSelect";
